import useApiRequest, { ApiResponse } from "@/lib/hooks/use-request";
import { CreateSaleRequestDto, SaleDetailsResponseDto, UpdateSaleRequestDto } from "@/lib/interfaces/sales";
import { Button } from "@/ui-components/ui/button";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { DollarSign } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";

const formSchema = z.object({
  title: z.string().min(1, "Titel ist erforderlich"),
  ownLead: z.boolean().optional(),
});

const SalesEditor = ({
  item,
  onSubmitDone,
  dealId,
}: {
  item?: SaleDetailsResponseDto;
  onSubmitDone?: () => void;
  dealId?: string;
}) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();
  type SalesFormSchema = z.infer<typeof formSchema>;

  const form = useForm<SalesFormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: item?.title ?? "",
      ownLead: item?.ownLead ?? false,
    },
  });

  const onSubmit = async (data: SalesFormSchema) => {
    let res: ApiResponse<SaleDetailsResponseDto>;
    if (item) {
      // Update existing product
      const req: UpdateSaleRequestDto = {
        title: data.title !== item.title ? data.title : undefined,
        ownLead: data.ownLead !== item.ownLead ? data.ownLead : undefined,
      };

      res = await apiRequest<SaleDetailsResponseDto>(`sales/${item._id}`, "PATCH", {
        body: req,
        toast: { toastText: "Verkaufsposition erfolgreich aktualisiert" },
      });
    } else {
      // Create new product
      const req: CreateSaleRequestDto = {
        title: data.title,
        ownLead: data.ownLead ?? false,
        belongsToDealId: dealId ?? "",
      };

      res = await apiRequest("sales", "POST", {
        body: req,
        toast: { toastText: "Verkaufsposition erfolgreich erstellt" },
      });
    }
    if (res.data) {
      if (onSubmitDone) {
        onSubmitDone();
      }
      navigate(`/sales/${res.data._id}`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel className="text-[16px] font-bold">Titel</FormLabel>
              <FormControl>
                <Input placeholder="Titel der Verkaufsposition" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="ownLead"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel className="text-[16px] font-bold">Eigener Lead</FormLabel>
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <DollarSign /> <span>{item ? "Verkaufsposition bearbeiten" : "Verkaufsposition erstellen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default SalesEditor;
